import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const GlowPortal = ({ children, to }) => (
  <Link to={to} className="relative inline-block group cursor-pointer">
    <span
      className="relative z-10 font-serif italic text-cream/80 transition-all duration-700 group-hover:text-gold"
      onMouseEnter={e => { e.currentTarget.style.textShadow = '0 0 8px rgba(212,175,55,0.9), 0 0 20px rgba(212,175,55,0.5)'; }}
      onMouseLeave={e => { e.currentTarget.style.textShadow = 'none'; }}
    >
      {children}
    </span>
    <span
      className="absolute bottom-0 left-0 h-[1px] w-0 group-hover:w-full transition-all duration-700 ease-out"
      style={{ background: 'linear-gradient(to right, #D4AF37, #FCF6BA, #D4AF37)' }}
    />
  </Link>
);

const BULLETS = [
  {
    text: (
      <>
        Die Verbindung ist <GlowPortal to="/beziehungsheilung-fuer-frauen">intensiv lebendig</GlowPortal> –
        aber sie lässt dich vollständig <GlowPortal to="/beziehungsheilung-fuer-frauen">ausgelaugt</GlowPortal> zurück.
      </>
    ),
  },
  {
    text: (
      <>
        Du <GlowPortal to="/beziehungsheilung-fuer-frauen">wartest auf seine Entscheidung</GlowPortal>,
        während dein eigenes Leben still an dir vorbeizieht.
      </>
    ),
  },
  {
    text: (
      <>
        Du bist eine starke Frau – und trotzdem{' '}
        <GlowPortal to="/female-leadership-fuer-frauen">verlierst du dich</GlowPortal>{' '}
        in dieser Verbindung. Immer wieder.
      </>
    ),
  },
  {
    text: (
      <>
        Du gibst, du <GlowPortal to="/beziehungsheilung-fuer-frauen">analysierst</GlowPortal>,
        du verstehst – aber die Gegenseitigkeit kommt nie an.
      </>
    ),
  },
  {
    text: (
      <>
        Loslassen fühlt sich an wie{' '}
        <GlowPortal to="/beziehungsheilung-fuer-frauen">dich selbst zu verlieren</GlowPortal>,
        nicht nur ihn. Die Angst ist real und lähmend.
      </>
    ),
  },
  {
    text: (
      <>
        Das{' '}
        <GlowPortal to="/beziehungsheilung-fuer-frauen">Anziehungs-und-Abstoßungs-Muster</GlowPortal>{' '}
        ist zur Grundeinstellung deines Nervensystems geworden.
      </>
    ),
  },
  {
    text: (
      <>
        Tief in dir <em>weißt</em> du:{' '}
        <GlowPortal to="/beziehungsheilung-fuer-frauen">So kann es nicht weitergehen.</GlowPortal>{' '}
        Etwas muss sich verändern – <span className="text-gold">jetzt.</span>
      </>
    ),
  },
];

const BulletItem = ({ bullet, index }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, x: -30 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.7, delay: index * 0.12 }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="flex items-start gap-6 p-6 cursor-default transition-all duration-700 relative group"
      style={{
        background: hovered ? 'rgba(212,175,55,0.04)' : 'transparent',
        borderLeft: `2px solid ${hovered ? '#D4AF37' : 'rgba(255,255,255,0.06)'}`,
        boxShadow: hovered ? '-4px 0 30px rgba(212,175,55,0.15)' : 'none',
        transition: 'all 0.7s ease',
      }}
    >
      <motion.div
        animate={hovered ? { scale: 1.3, boxShadow: '0 0 20px rgba(212,175,55,0.8)' } : { scale: 1, boxShadow: '0 0 0px rgba(212,175,55,0)' }}
        transition={{ duration: 0.5 }}
        className="w-2 h-2 rounded-full bg-gold mt-2 shrink-0"
      />
      <p
        className="text-lg font-light leading-relaxed"
        style={{ color: hovered ? 'rgba(249,249,249,0.95)' : 'rgba(249,249,249,0.55)', transition: 'color 0.7s ease' }}
      >
        {bullet.text}
      </p>
    </motion.div>
  );
};

const MirrorOfTruth = () => (
  <section className="relative py-28 bg-darker overflow-hidden border-t border-white/5">
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_80%_50%,rgba(212,175,55,0.04)_0%,transparent_60%)]" />
    <div className="relative z-10 max-w-5xl mx-auto px-6">
      <div className="text-center mb-20">
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-gold tracking-[0.3em] uppercase text-xs mb-6 font-semibold"
        >
          Der Spiegel der Wahrheit
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className="text-4xl md:text-6xl font-serif text-cream leading-tight"
        >
          Das ist für dich,{' '}
          <span className="italic text-transparent bg-clip-text bg-gold-gradient">wenn...</span>
        </motion.h2>
      </div>

      <div className="space-y-2 max-w-3xl mx-auto">
        {BULLETS.map((b, i) => <BulletItem key={i} bullet={b} index={i} />)}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.8 }}
        className="mt-16 p-10 text-center border border-gold/20 relative overflow-hidden"
        style={{ background: 'rgba(212,175,55,0.03)' }}
      >
        <div
          className="absolute top-0 left-0 right-0 h-[1px]"
          style={{ background: 'linear-gradient(to right, transparent, #D4AF37, transparent)' }}
        />
        <p className="text-2xl md:text-3xl font-serif text-cream italic">
          "Wenn du diesen Zug der Wiedererkennung gespürt hast –{' '}
          <span className="text-gold">hat deine Seele bereits Ja gesagt."</span>
        </p>
      </motion.div>
    </div>
  </section>
);

export default MirrorOfTruth;